import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { cardContentItemContent$key } from "~/queries/__generated__/cardContentItemContent.graphql";

import { Container, Content, Header, StyledCoverEntityLink, StyledTitleEntityLink } from "./styles";

export type Props = {
  reference: cardContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemCard = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_ }, reference) => {
    const entity = useFragment(
      graphql`
        fragment cardContentItemContent on Content {
          ...entityLink
          ...hooksContentItemProvidersContent
            @arguments(
              withCoverImage: true
              withCoverImageSize540x360: true
              withHeadline: true
              withSponsorTag: true
            )
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(entity);
    const header = (
      <StyledCoverEntityLink reference={entity}>
        {providers.coverImage({
          responsiveVariants: {
            desktopUp: "size540x360",
            mobileUp: "size540x360",
            tabletUp: "size540x360",
          },
        })}
      </StyledCoverEntityLink>
    );
    const content = (
      <StyledTitleEntityLink reference={entity}>
        {providers.headline({ hideTooltip: true })}
      </StyledTitleEntityLink>
    );
    const footer = providers.sponsorTag?.({
      color: "#001246",
      gap: "4px",
      imageVariant: "small",
      layoutDirection: "vertical",
      nameFontSize: "16px",
      typeFontSize: "12px",
    });

    return (
      <Container className={className} ref={reference}>
        <Header>{header}</Header>
        <Content>
          {content} {footer}
        </Content>
      </Container>
    );
  },
);

ContentItemCard.displayName = "ContentItemCard";
